import React, { useContext } from "react";
import { makeStyles } from "@material-ui/core";
import { orange } from "@material-ui/core/colors";
import WarningIcon from "@material-ui/icons/Warning";
import { WhatsAppsContext } from "../../context/WhatsApp/WhatsAppsContext";
import { i18n } from "../../translate/i18n";
import { isDarkTheme, tintStyle } from "../../helpers/darkTint";

// Canais sem socket Baileys têm status forçado para CONNECTED no backend
// (helpers/connectionChannels.ts) — o aviso só faz sentido para conexão Baileys.
const NON_SOCKET_CHANNELS = ["cloudapi", "email", "instagram", "sms"];

const useStyles = makeStyles((theme) => ({
  banner: {
    display: "flex",
    alignItems: "center",
    gap: 8,
    padding: "8px 16px",
    ...tintStyle(isDarkTheme(theme), orange, 50, 900),
    borderBottom: `1px solid ${isDarkTheme(theme) ? theme.palette.divider : "#ffe0b2"}`,
    fontSize: 13,
  },
  icon: {
    fontSize: 18,
    flexShrink: 0,
  },
}));

const isConnected = (status) => String(status || "").toUpperCase() === "CONNECTED";

/**
 * Aviso no chamado cuja conexão NÃO está conectada: as respostas não saem até a
 * conexão voltar ou o chamado ser transferido para outra conexão. Caso típico:
 * ticket de grupo que ficou preso numa conexão que foi desconectada (não movemos
 * o chamado automaticamente — decisão do usuário em 25/09/2026).
 *
 * O status vem ao vivo do WhatsAppsContext (eventos de sessão); sem entrada
 * lá, usa o objeto `whatsapp` do próprio ticket. Sem status conhecido, não
 * mostra nada para não dar alarme falso.
 */
const ConnectionDisconnectedBanner = ({ whatsappId, whatsapp, ticketStatus }) => {
  const classes = useStyles();
  const { whatsApps } = useContext(WhatsAppsContext) || {};

  if (ticketStatus === "closed") return null;

  const id = Number(whatsappId || whatsapp?.id);
  if (!id) return null;

  const live = Array.isArray(whatsApps)
    ? whatsApps.find((w) => Number(w.id) === id)
    : null;
  const connection = live || whatsapp;
  if (!connection || !connection.status) return null;

  const type = String(connection.type || "baileys");
  if (NON_SOCKET_CHANNELS.includes(type)) return null;
  if (isConnected(connection.status)) return null;

  const name = connection.name || `#${id}`;

  return (
    <div className={classes.banner} role="alert">
      <WarningIcon className={classes.icon} />
      <span>
        {i18n.t("connectionDisconnectedBanner.message", { name })}
      </span>
    </div>
  );
};

export default ConnectionDisconnectedBanner;
